// When Home is allowed to show its cards, and the local snapshots that let it
// do so at once.
//
// Home's body is decided by a CHAIN of answers, not one: /me/status says
// whether you are in a round, /home-feed says who else is out, and only then
// can the page ask for that round's live board and its format — and, for a
// matchplay day, its matches. Rendering each card as its own answer landed
// painted recent form, then swapped it for a board, then for a set of matches,
// with every late card running its own entrance. So the body is held until the
// answers that decide its SHAPE are in, and rises once. Pure, so the gate can
// be pinned (homeReveal.test.js).

// Past this the body shows whatever it has; anything still undecided draws its
// own placeholder rather than a guess. Long enough to cover a warm request,
// short enough that a slow radio never reads as a blank page.
export const REVEAL_CAP_MS = 1200;

// A query has ANSWERED once it has data or has given up. An error counts:
// nothing more is coming, and the card shows its last good data or its
// placeholder. 'idle' (disabled) and 'loading' have not answered.
export function hasAnswered(query) {
  if (!query) return false;
  return query.status === 'success' || query.status === 'error';
}

/**
 * What is still undecided. Every argument is a plain flag so the rule can be
 * tested without React Query.
 *
 *   status        — has /me/status answered (not 'loading')
 *   rounds        — has the player's round list answered (true if no player)
 *   feed          — has /home-feed answered (true if no society)
 *   boardTid      — the tournament the middle slot may feature, or null
 *   live, format  — have that tournament's live board and format answered
 *   matchplay     — is it a matchplay tournament (only known once `format` is)
 *   matches       — have its matches answered
 *   summaryNeeded — is the summary board a fallback here (you are in the round)
 *   summary       — has that summary answered
 */
export function homeReadiness({
  status, rounds, feed, boardTid,
  live, format, matchplay, matches, summaryNeeded, summary,
}) {
  // The middle slot cannot say what it is until the tournament it might
  // feature has told us whether golf is being played on it and in what form.
  const slotPending = !!boardTid && (
    !live || !format || (matchplay && !matches) || (summaryNeeded && !summary)
  );
  return {
    statusPending: !status,
    roundsPending: !rounds,
    feedPending: !feed,
    slotPending,
    ready: !!status && !!rounds && !!feed && !slotPending,
  };
}

/* ─── Snapshots ───────────────────────────────────────────── */
// `{ at, value }` under one key. `at` is handed to React Query as
// `initialDataUpdatedAt`: a snapshot is as old as the moment it was written,
// not the moment it was read, so it is revalidated instead of being trusted as
// brand new for the length of `staleTime`.

export function readSnapshot(key, ttlMs) {
  if (!key) return undefined;
  try {
    const c = JSON.parse(localStorage.getItem(key) || 'null');
    if (!c || !c.at || c.value === undefined) return undefined;
    if (Date.now() - c.at > ttlMs) return undefined;
    return c;
  } catch { return undefined; }
}

export function writeSnapshot(key, value) {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify({ at: Date.now(), value })); } catch {}
}
